import { useEffect, useRef, useState } from "react";

const streamingEndpoint = "https://kurukwin.xyz/sumit-api/exchange/member/playerService/getStreamingUrlByEventId";
const mediaUrl = (value) => typeof value === "string" ? value.trim() : "";

function MediaFrame({ src, title, height, scoreboard = false }) {
  const frame = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!scoreboard) return;
    let origin;
    try {
      origin = new URL(src, window.location.href).origin;
    } catch {
      return;
    }
    const resize = (event) => {
      if (event.origin !== origin || event.source !== frame.current?.contentWindow) return;
      const nextHeight = Number(event.data?.height);
      if (Number.isFinite(nextHeight) && nextHeight >= 80 && nextHeight <= 1000 && frame.current) {
        frame.current.style.height = `${nextHeight}px`;
      }
    };
    window.addEventListener("message", resize);
    return () => window.removeEventListener("message", resize);
  }, [src, scoreboard]);

  if (failed) return <p className="analytics-empty">
    {scoreboard ? "Scoreboard unavailable for this match." : "Live video unavailable for this match."}
  </p>;

  return <div className="analytics-media-frame">
    {!loaded && <p className="analytics-media-loading" role="status">
      {scoreboard ? "Loading scoreboard..." : "Loading live TV..."}
    </p>}
    <iframe
      ref={frame}
      title={title}
      src={src}
      scrolling="no"
      sandbox={scoreboard ? "allow-scripts allow-same-origin" : undefined}
      referrerPolicy="strict-origin-when-cross-origin"
      allow={scoreboard ? undefined : "autoplay; fullscreen; picture-in-picture"}
      allowFullScreen={!scoreboard}
      style={{ height, opacity: loaded ? 1 : 0 }}
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
    />
  </div>;
}

function LiveTv({ detail }) {
  const [stream, setStream] = useState({ url: "", loading: false });
  const { allowCustomTv, eventId } = detail;

  useEffect(() => {
    if (!allowCustomTv || eventId == null || eventId === "") return;
    const controller = new AbortController();
    setStream({ url: "", loading: true });
    const load = async () => {
      try {
        const query = new URLSearchParams({ eventId: String(eventId), isPopup: "false" });
        const response = await fetch(`${streamingEndpoint}?${query}`, {
          signal: controller.signal,
          credentials: "omit",
        });
        if (!response.ok) throw new Error("Unable to load stream.");
        const data = await response.json();
        const url = data?.status === false ? "" : mediaUrl(data?.streamingUrl);
        if (!controller.signal.aborted) setStream({ url, loading: false });
      } catch {
        if (!controller.signal.aborted) setStream({ url: "", loading: false });
      }
    };
    load();
    return () => controller.abort();
  }, [allowCustomTv, eventId]);

  if (allowCustomTv && stream.loading) {
    return <p className="analytics-empty" role="status">Loading live TV...</p>;
  }
  const url = allowCustomTv ? stream.url : mediaUrl(detail.adsContent);
  return url ? <MediaFrame key={url} src={url} title="Live TV Player" height={233} /> :
    <p className="analytics-empty">Live video unavailable for this match.</p>;
}

export default function MatchMedia({ detail }) {
  const scoreUrl = mediaUrl(detail.scoreURL);
  const hasTv = detail.status === "in_play" && (detail.allowCustomTv
    ? detail.eventId != null && detail.eventId !== ""
    : Boolean(mediaUrl(detail.adsContent)));
  const [tab, setTab] = useState(scoreUrl ? "scoreboard" : "live");

  if (!scoreUrl && !hasTv) return null;
  return <section className="analytics-media" aria-label="Live match">
    <div className="function-wrap">
      <div className="analytics-actions">
        <button type="button" className={`btn-send ${tab === "scoreboard" ? "selected" : ""}`}
          onClick={() => setTab("scoreboard")}>ScoreBoard</button>
        <button type="button" className={`btn-send ${tab === "live" ? "selected" : ""}`}
          onClick={() => setTab("live")}>Live</button>
      </div>
    </div>
    {tab === "scoreboard" ? (scoreUrl ?
      <MediaFrame key={scoreUrl} src={scoreUrl} title="Match scoreboard"
        height={detail.screenScore === "full" ? 270 : 177} scoreboard /> :
      <p className="analytics-empty">Scoreboard unavailable for this match.</p>
    ) : hasTv ? <LiveTv key={`${detail.eventId}:${Boolean(detail.allowCustomTv)}`} detail={detail} /> :
      <p className="analytics-empty">Live video unavailable for this match.</p>}
  </section>;
}
